import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReceiptIcon from '@mui/icons-material/Receipt';
import dayjs from 'dayjs';
import useBon from '../../hooks/useBon';
import { useAuth } from '../../context/authContext';
import useHttp from '../../hooks/useHttp';
import { toast } from 'react-toastify';
import useCustomer from '../../hooks/useCustomer';
import LockIconWrapper from '../components/LockIconWrapper';
import EditableList from '../EditableList/EditableList';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function formatEuroCents(cents) {
  const v = (Number(cents || 0) / 100).toFixed(2).replace('.', ',');
  return `${v} €`;
}

const BonList = () => {
  const { getBonDetails, convertBonToInvoice } = useBon();
  const auth = useAuth();
  const { sendRequest } = useHttp();
  const { searchCustomers } = useCustomer();

  const [entertainmentOptions, setEntertainmentOptions] = useState([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsData, setDetailsData] = useState(null);

  const [convertOpen, setConvertOpen] = useState(false);
  const [convertBon, setConvertBon] = useState(null);
  const [customerNumberInput, setCustomerNumberInput] = useState('');
  const [hasLoadedCustomer, setHasLoadedCustomer] = useState(false);
  const [customerData, setCustomerData] = useState({
    company: '',
    firstName: '',
    lastName: '',
    mail: '',
    street: '',
    postcode: '',
    city: '',
    addressAddition: '',
    jobCode: '',
  });

  const listParams = useMemo(() => ({ limit: 25 }), []);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await sendRequest({
          url: '/api/v1/entertainment',
          method: 'GET',
          params: { light: 'true' },
          headers: auth.getAuthHeaders(),
        });
        if (Array.isArray(data)) {
          setEntertainmentOptions(
            data.map(e => ({
              value: e._id,
              label: e.longName || e.entertainmentName || e._id,
            }))
          );
        }
      } catch {
        // non-critical, filter dropdown stays empty
      }
    };
    load();
  }, []);

  const canConvert = auth.checkRightAny('bon:convert');
  const canReadCustomers = auth.checkRight('customer:read');

  const handleConvert = async bon => {
    try {
      if (!bon || bon.personalNumber) return;
      setConvertBon(bon);
      setCustomerNumberInput(bon.customerNumber ? String(bon.customerNumber) : '');
      setCustomerData({
        company: '',
        firstName: '',
        lastName: '',
        mail: '',
        street: '',
        postcode: '',
        city: '',
        addressAddition: '',
        jobCode: '',
      });
      setHasLoadedCustomer(false);
      setConvertOpen(true);
    } catch (e) {
      // useHttp already toasts errors
    }
  };

  const openDetails = async bonId => {
    try {
      const res = await getBonDetails(bonId);
      setDetailsData(res);
      setDetailsOpen(true);
    } catch (e) {
      // error handled globally
    }
  };

  const handleLookupCustomer = async () => {
    try {
      const num = parseInt(customerNumberInput, 10);
      if (Number.isNaN(num)) {
        toast.error('Ungültige Kundennummer');
        return;
      }
      const res = await searchCustomers(String(num), 1, 5);
      const match = (res?.customers || []).find(c => c.customerNumber === num);
      if (!match) {
        toast.error('Kunde nicht gefunden');
        setHasLoadedCustomer(false);
        return;
      }
      setCustomerData({
        company: match.companyName || '',
        firstName: match.firstName || '',
        lastName: match.lastName || '',
        mail: match.email || '',
        street:
          match.street && match.houseNumber
            ? `${match.street} ${match.houseNumber}`
            : match.street || '',
        postcode: match.postalCode || '',
        city: match.city || '',
        addressAddition: match.addressAddition || '',
        jobCode: match.jobCode || '',
      });
      toast.success('Kundendaten geladen');
      setHasLoadedCustomer(true);
    } catch (e) {}
  };

  const handleSubmitConversion = async () => {
    try {
      if (!convertBon) return;
      const num = parseInt(customerNumberInput, 10);
      const hasNumber = !Number.isNaN(num);
      const payload =
        hasNumber && hasLoadedCustomer
          ? { customerNumber: num }
          : { customerData: { ...customerData } };
      await convertBonToInvoice(convertBon._id, payload);
      setConvertOpen(false);
      setConvertBon(null);
      window.dispatchEvent(new CustomEvent('editableListRefresh', { detail: { resource: 'bon' } }));
    } catch (e) {}
  };

  const bonSchema = useMemo(
    () => ({
      soldAt: {
        label: 'Datum',
        type: 'text',
        readOnly: true,
        minWidth: '160px',
        listDisplayValue: value => (value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—'),
        sortType: 'date',
        sortValue: value => (value ? Date.parse(String(value)) : null),
        filterType: 'date',
        filterValue: value => (value ? dayjs(value).format('YYYY-MM-DD') : ''),
      },
      entertainmentName: {
        label: 'Entertainment',
        type: 'text',
        readOnly: true,
        minWidth: '200px',
        filterType: 'select',
        options: entertainmentOptions,
      },
      receiptNumber: {
        label: 'Bon-Nr.',
        type: 'text',
        readOnly: true,
        minWidth: '120px',
      },
      grossCents: {
        label: 'Gesamt brutto',
        type: 'number',
        readOnly: true,
        minWidth: '140px',
        listDisplayValue: value => formatEuroCents(value),
        sortType: 'number',
        sortValue: value => (Number.isFinite(Number(value)) ? Number(value) : null),
        filterType: 'number',
        filterValue: value => Number(value || 0) / 100,
      },
      registerNumber: {
        label: 'Kasse',
        type: 'text',
        readOnly: true,
        minWidth: '90px',
      },
      type: {
        label: 'Typ',
        type: 'text',
        readOnly: true,
        minWidth: '170px',
      },
      cashier: {
        label: 'Kassierer',
        type: 'text',
        readOnly: true,
        minWidth: '160px',
      },
    }),
    [entertainmentOptions]
  );

  const listConfig = useMemo(
    () => ({
      endpoint: '/api/v1/bon/list',
      searchParam: 'receiptNumber',
      resultsKey: 'items',
    }),
    []
  );

  const transformItems = useCallback(item => {
    return {
      ...item,
      entertainmentName: item?.entertainment?.longName || item?.entertainment?.name || '—',
      grossCents: item?.totals?.grossCents ?? 0,
    };
  }, []);

  return (
    <Box sx={viewRootTextPrimarySx}>
      <EditableList
        schema={bonSchema}
        title='Bon-Liste'
        resourceType='bon'
        searchPlaceholder='Bons durchsuchen (Bon-Nr.)'
        hideColumnSelector={true}
        listParams={listParams}
        visibleColumnKeys={[
          'soldAt',
          'entertainmentName',
          'receiptNumber',
          'grossCents',
          'registerNumber',
          'type',
          'cashier',
        ]}
        options={{
          disableCreate: true,
          disableDelete: true,
          disableDetailView: true,
          listConfig,
          transformItems,
        }}
        renderRowActions={row => {
          return (
            <>
              <Tooltip title='Details anzeigen'>
                <IconButton
                  size='small'
                  onClick={e => {
                    e.stopPropagation();
                    openDetails(row._id);
                  }}
                >
                  <VisibilityIcon fontSize='small' />
                </IconButton>
              </Tooltip>
              {canConvert && !row.personalNumber && !row.convertedToInvoice && (
                <Tooltip title='Rechnung erstellen'>
                  <IconButton
                    size='small'
                    onClick={e => {
                      e.stopPropagation();
                      handleConvert(row);
                    }}
                  >
                    <LockIconWrapper right='bon:convert'>
                      <ReceiptIcon fontSize='small' />
                    </LockIconWrapper>
                  </IconButton>
                </Tooltip>
              )}
            </>
          );
        }}
      />

      {/* Details Modal */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} fullWidth maxWidth='md'>
        <DialogTitle>Bon-Details</DialogTitle>
        <DialogContent dividers>
          {detailsData ? (
            <Box>
              <Typography variant='subtitle2' gutterBottom>
                {detailsData.entertainment?.longName ||
                  detailsData.entertainment?.entertainmentName ||
                  '—'}{' '}
                | Kasse {detailsData.registerNumber} | Bon {detailsData.receiptNumber}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Artikel</TableCell>
                    <TableCell>Artikelnr.</TableCell>
                    <TableCell align='right'>Menge</TableCell>
                    <TableCell align='right'>Brutto/Einheit</TableCell>
                    <TableCell align='right'>MwSt.%</TableCell>
                    <TableCell align='right'>MwSt. €</TableCell>
                    <TableCell align='right'>Gesamt</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(detailsData.positions || []).map((p, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{p.articleName || '—'}</TableCell>
                      <TableCell>{p.copagoArticleId || p.articleCopagoId || '—'}</TableCell>
                      <TableCell align='right'>{p.netQuantity}</TableCell>
                      <TableCell align='right'>{formatEuroCents(p.unitPriceCents)}</TableCell>
                      <TableCell align='right'>{p.vatRatePercent}%</TableCell>
                      <TableCell align='right'>{formatEuroCents(p.vatAmountCents)}</TableCell>
                      <TableCell align='right'>{formatEuroCents(p.netGrossCents)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Typography>Wird geladen…</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Convert Dialog */}
      <Dialog open={convertOpen} onClose={() => setConvertOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle>Rechnung erstellen</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {canReadCustomers && (
              <>
                <Grid item xs={8}>
                  <TextField
                    size='small'
                    label='Kundennummer'
                    fullWidth
                    value={customerNumberInput}
                    onChange={e => {
                      setCustomerNumberInput(e.target.value);
                      if (!e.target.value) {
                        setHasLoadedCustomer(false);
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Button
                    variant='outlined'
                    fullWidth
                    onClick={handleLookupCustomer}
                    sx={{ height: '100%' }}
                  >
                    Kunde laden
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Divider>Oder manuell eingeben</Divider>
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <TextField
                size='small'
                label='Firma'
                fullWidth
                value={customerData.company}
                onChange={e => setCustomerData(prev => ({ ...prev, company: e.target.value }))}
                disabled={hasLoadedCustomer}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                size='small'
                label='Vorname'
                fullWidth
                value={customerData.firstName}
                onChange={e => setCustomerData(prev => ({ ...prev, firstName: e.target.value }))}
                disabled={hasLoadedCustomer}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                size='small'
                label='Nachname'
                fullWidth
                value={customerData.lastName}
                onChange={e => setCustomerData(prev => ({ ...prev, lastName: e.target.value }))}
                disabled={hasLoadedCustomer}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size='small'
                label='E-Mail'
                fullWidth
                value={customerData.mail}
                onChange={e => setCustomerData(prev => ({ ...prev, mail: e.target.value }))}
                disabled={hasLoadedCustomer}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size='small'
                label='Straße und Hausnummer'
                fullWidth
                value={customerData.street}
                onChange={e => setCustomerData(prev => ({ ...prev, street: e.target.value }))}
                disabled={hasLoadedCustomer}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                size='small'
                label='PLZ'
                fullWidth
                value={customerData.postcode}
                onChange={e => setCustomerData(prev => ({ ...prev, postcode: e.target.value }))}
                disabled={hasLoadedCustomer}
              />
            </Grid>
            <Grid item xs={8}>
              <TextField
                size='small'
                label='Ort'
                fullWidth
                value={customerData.city}
                onChange={e => setCustomerData(prev => ({ ...prev, city: e.target.value }))}
                disabled={hasLoadedCustomer}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size='small'
                label='Adresszusatz'
                fullWidth
                value={customerData.addressAddition}
                onChange={e =>
                  setCustomerData(prev => ({ ...prev, addressAddition: e.target.value }))
                }
                disabled={hasLoadedCustomer}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size='small'
                label='Referenz (Job-Code)'
                fullWidth
                value={customerData.jobCode}
                onChange={e => setCustomerData(prev => ({ ...prev, jobCode: e.target.value }))}
                disabled={hasLoadedCustomer}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertOpen(false)}>Abbrechen</Button>
          <Button variant='contained' onClick={handleSubmitConversion} disabled={!convertBon}>
            <LockIconWrapper right='bon:convert'>Rechnung erstellen</LockIconWrapper>
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BonList;
